import { useEffect, useRef } from 'react';
import { trackPageEvent } from '../services/analyticsService';
import type { PageType } from '../types/analytics';

interface UsePageTrackingOptions {
  pageType: PageType;
  slug: string;
  enabled?: boolean;
}

export function usePageTracking({ pageType, slug, enabled = true }: UsePageTrackingOptions) {
  const hasTrackedView = useRef(false);
  const maxScrollDepth = useRef(0);
  const hasTrackedScroll = useRef(false);

  useEffect(() => {
    if (!enabled || !slug || hasTrackedView.current) return;

    hasTrackedView.current = true;

    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source') || params.get('source') || null;

    trackPageEvent({
      pageType,
      slug,
      eventType: 'page_view',
      referrer: document.referrer || null,
      utmSource,
      userAgent: navigator.userAgent,
    });
  }, [pageType, slug, enabled]);

  useEffect(() => {
    if (!enabled || !slug) return;

    const updateMaxScrollDepth = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

      if (scrollHeight <= 0) {
        maxScrollDepth.current = 100;
        return;
      }

      const scrollPercent = Math.round((scrollTop / scrollHeight) * 100);
      maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);
    };

    const sendScrollEvent = () => {
      if (hasTrackedScroll.current || maxScrollDepth.current === 0) return;
      hasTrackedScroll.current = true;

      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source') || params.get('source') || null;

      trackPageEvent({
        pageType,
        slug,
        eventType: 'page_view',
        maxScrollDepth: maxScrollDepth.current,
        referrer: document.referrer || null,
        utmSource,
        userAgent: navigator.userAgent,
      });
    };

    const handleScroll = () => {
      updateMaxScrollDepth();
    };

    const handleBeforeUnload = () => {
      sendScrollEvent();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        sendScrollEvent();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    updateMaxScrollDepth();

    return () => {
      sendScrollEvent();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pageType, slug, enabled]);
}
